/**
 * Lightweight PostHog tracker — direct HTTP capture API.
 *
 * Why no `posthog-js` SDK?
 *   The Emergent platform badge already loads a singleton `posthog-js` on
 *   the page with its own project key. Our second `posthog.init(...)` is
 *   subject to subtle named-instance quirks across SDK versions, so capture
 *   calls don't reliably fire. Posting directly to PostHog's HTTP capture
 *   endpoint is the simplest, most reliable, and lightest-weight path for
 *   the few events we want to track.
 *
 * Endpoint: POST {host}/i/v0/e/
 *   Body: { api_key, event, distinct_id, properties }
 *
 * Behavior:
 *   - No-ops silently if the env vars are missing or DNT is enabled.
 *   - Generates and persists a `distinct_id` in `localStorage` (uuid v4).
 *   - Uses `keepalive: true` so events fire even mid-navigation (e.g. CTA
 *     clicks that immediately route to /register).
 *   - Never throws — analytics must never break the UX.
 */

const STORAGE_KEY = 'fs_distinct_id';

let key = null;
let host = null;
let distinctId = null;
let dntOptOut = false;

const generateUUID = () => {
  // RFC 4122 v4-ish; good enough for analytics distinct_id, no crypto needs.
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const getOrCreateDistinctId = () => {
  try {
    const existing = localStorage.getItem(STORAGE_KEY);
    if (existing) return existing;
    const fresh = generateUUID();
    localStorage.setItem(STORAGE_KEY, fresh);
    return fresh;
  } catch (_e) {
    // Private mode or storage blocked — generate a transient id.
    return generateUUID();
  }
};

export const initAnalytics = () => {
  if (typeof window === 'undefined') return;
  if (key) return;

  const k = process.env.REACT_APP_POSTHOG_KEY;
  const h = process.env.REACT_APP_POSTHOG_HOST;
  if (!k || !h) return;

  // Respect Do-Not-Track.
  try {
    const dnt =
      navigator.doNotTrack === '1' ||
      window.doNotTrack === '1' ||
      navigator.msDoNotTrack === '1';
    if (dnt) {
      dntOptOut = true;
      return;
    }
  } catch (_e) {
    /* ignore */
  }

  key = k;
  host = h.replace(/\/$/, '');
  distinctId = getOrCreateDistinctId();
};

export const track = (event, properties) => {
  if (dntOptOut) return;
  if (!key || !host || !distinctId) return;
  if (typeof fetch === 'undefined') return;

  const body = {
    api_key: key,
    event,
    distinct_id: distinctId,
    properties: {
      ...(properties || {}),
      $current_url:
        typeof window !== 'undefined' ? window.location.href : undefined,
      $pathname:
        typeof window !== 'undefined'
          ? window.location.pathname
          : undefined,
    },
    timestamp: new Date().toISOString(),
  };

  try {
    fetch(`${host}/i/v0/e/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
      credentials: 'omit',
      mode: 'cors',
    }).catch(() => {
      /* swallow network errors */
    });
  } catch (_e) {
    /* never throw */
  }
};

export default { initAnalytics, track };
